import { defineStore } from 'pinia';
import { positionService } from '@/services/positionService';
import type { 
  Position, 
  PositionQueryParams, 
  PositionSearchParams 
} from '@/types/position';
import { ElMessage } from 'element-plus';

interface PositionState {
  positions: Position[];
  currentPosition: Position | null;
  loading: boolean;
  total: number;
  page: number;
  size: number;
}

export const usePositionStore = defineStore('position', {
  state: (): PositionState => ({
    positions: [],
    currentPosition: null,
    loading: false,
    total: 0,
    page: 0,
    size: 10
  }),
  
  actions: {
    // 创建职位
    async createPosition(position: Position) {
      try {
        this.loading = true;
        const response = await positionService.createPosition(position);
        if (response.code === 200) {
          ElMessage.success('职位创建成功');
          return response.data;
        } else {
          ElMessage.error(response.message || '创建失败');
        }
      } catch (error: any) {
        ElMessage.error(error.response?.data?.message || '创建职位时发生错误');
        throw error;
      } finally {
        this.loading = false;
      }
    },

    // 更新职位
    async updatePosition(position: Position) {
      try {
        this.loading = true;
        const response = await positionService.updatePosition(position);
        if (response.code === 200) {
          ElMessage.success('职位更新成功');
          // 更新当前列表中的职位
          const index = this.positions.findIndex(p => p.id === position.id);
          if (index !== -1) {
            this.positions[index] = response.data;
          }
          return response.data;
        } else {
          ElMessage.error(response.message || '更新失败');
        }
      } catch (error: any) {
        ElMessage.error(error.response?.data?.message || '更新职位时发生错误');
        throw error;
      } finally {
        this.loading = false;
      }
    },

    // 删除职位
    async deletePosition(id: number) {
      try {
        this.loading = true;
        const response = await positionService.deletePosition(id);
        if (response.code === 200) {
          ElMessage.success('职位删除成功');
          // 从列表中移除
          this.positions = this.positions.filter(p => p.id !== id);
          return true;
        } else {
          ElMessage.error(response.message || '删除失败');
          return false;
        }
      } catch (error: any) {
        ElMessage.error(error.response?.data?.message || '删除职位时发生错误');
        throw error;
      } finally {
        this.loading = false;
      }
    },

    // 获取单个职位详情
    async getPosition(id: number) {
      try {
        this.loading = true;
        const response = await positionService.getPosition(id);
        if (response.code === 200) {
          this.currentPosition = response.data;
          return response.data;
        } else {
          ElMessage.error(response.message || '获取职位详情失败');
        }
      } catch (error: any) {
        ElMessage.error(error.response?.data?.message || '获取职位详情时发生错误');
        throw error;
      } finally {
        this.loading = false;
      }
    },

    // 获取公司下的职位列表
    async getPositionsByCompany(companyId: number, page = 1, size = 10) {
      try {
        this.loading = true;
        const response = await positionService.getPositionsByCompany(companyId, page, size);
        if (response.code === 200) {
          this.positions = response.data.content;
          this.total = response.data.totalElements;
          this.page = response.data.number;
          this.size = response.data.size;
          return response.data;
        } else {
          ElMessage.error(response.message || '获取职位列表失败');
        }
      } catch (error: any) {
        ElMessage.error(error.response?.data?.message || '获取职位列表时发生错误');
        throw error;
      } finally {
        this.loading = false;
      }
    },

    // 条件查询职位
    async queryPositions(params: PositionQueryParams) {
      try {
        this.loading = true;
        const queryParams = {
          ...params,
          page: params.page !== undefined ? params.page : this.page,
          size: params.size !== undefined ? params.size : this.size
        };
        const response = await positionService.queryPositions(queryParams);
        if (response.code === 200) {
          this.positions = response.data.content;
          this.total = response.data.totalElements;
          this.page = response.data.number;
          this.size = response.data.size;
          return response.data;
        } else {
          ElMessage.error(response.message || '查询职位列表失败');
        }
      } catch (error: any) {
        ElMessage.error(error.response?.data?.message || '查询职位列表时发生错误');
        throw error;
      } finally {
        this.loading = false;
      }
    },

    // 关键字搜索职位
    async searchPositions(params: PositionSearchParams) {
      try {
        this.loading = true;
        const searchParams = {
          ...params,
          page: params.page !== undefined ? params.page : this.page,
          size: params.size !== undefined ? params.size : this.size
        };
        const response = await positionService.searchPositions(searchParams);
        if (response.code === 200) {
          this.positions = response.data.content;
          this.total = response.data.totalElements;
          this.page = response.data.number;
          this.size = response.data.size;
          return response.data;
        } else {
          ElMessage.error(response.message || '搜索职位失败');
        }
      } catch (error: any) {
        ElMessage.error(error.response?.data?.message || '搜索职位时发生错误');
        throw error;
      } finally {
        this.loading = false;
      }
    },

    // 重置状态
    resetState() {
      this.positions = [];
      this.currentPosition = null;
      this.total = 0;
      this.page = 0;
    }
  }
}); 